import type { FacePhotoSize } from '@repo/api-contracts';
import { type ReactElement, useId, useRef } from 'react';
import { Button } from '@/shared/ui';
import type { FaceAssetUploadState } from '../model/use-face-asset-upload';

/** 등록된 에셋의 미리보기 — 표시 URL 은 만료되면 없다(그때는 미등록 문구 대신 빈 칸이 아니라 같은 미등록 자리를 쓴다). */
interface AssetPreview {
  readonly url: string | undefined;
  readonly alt: string;
  readonly size: FacePhotoSize;
}

interface AssetUploadControlsProps {
  /** 접근성 이름의 대상 — 사진 카드 '검정 앞면 Front', 합성 칸 '앞면 Front 마스크'. 다시 업로드·취소·삭제 aria-label 앞에 붙는다. */
  readonly label: string;
  /** 단계 문구의 파일 종류 — 사진 카드 '사진', 합성 칸은 에셋 종류 이름(마스크·음영·안내선). */
  readonly subject: string;
  /**
   * 파일 라벨·업로드 버튼에 붙는 종류 이름. 사진 카드는 '사진'('… 사진 파일'·'사진 교체'), 합성 칸은 label 에 종류가
   * 이미 들어 있어 붙이지 않는다('… 파일'·'교체').
   */
  readonly actionNoun?: string;
  readonly accept: string;
  readonly preview: AssetPreview | null;
  readonly emptyText: string;
  /** 미리보기·버튼을 작게 둔다 — 면 카드 안에 종류별로 나란히 놓이는 합성 칸. */
  readonly compact?: boolean;
  /** 업로드를 잠근다(마스크 없는 면의 음영·안내선). 진행 중 취소·재시도·삭제는 잠그지 않는다. */
  readonly disabled?: boolean;
  readonly state: FaceAssetUploadState;
  readonly onUpload: (file: File) => void;
  readonly onRetry: () => void;
  readonly onCancel: () => void;
  /** 있으면 등록된 에셋에 '삭제' 버튼을 둔다(합성 칸). 사진 카드는 교체만 한다. */
  readonly onRemove?: () => void;
}

const BUSY_PHASES: readonly FaceAssetUploadState['phase'][] = [
  'uploading',
  'reading-size',
  'confirming',
];

/**
 * 면 사진 카드(FacePhotoCard)와 합성 에셋 칸(CompositeAssetSlot)이 공유하는 업로드 조작 — 체크무늬 미리보기·px 크기·
 * 파일 input·업로드(교체)/다시 시도/취소/삭제 버튼 줄·단계 문구·오류 문구. 업로드 훅(useFaceAssetUpload)의 상태를 그대로
 * 받아 두 화면의 문구·스타일이 한 곳에서 바뀐다.
 */
export const AssetUploadControls = ({
  label,
  subject,
  actionNoun,
  accept,
  preview,
  emptyText,
  compact = false,
  disabled = false,
  state,
  onUpload,
  onRetry,
  onCancel,
  onRemove,
}: AssetUploadControlsProps): ReactElement => {
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const busy = BUSY_PHASES.includes(state.phase);
  const fileLabel = actionNoun ? `${label} ${actionNoun}` : label;
  const verb = preview ? '교체' : '업로드';
  const buttonSize = compact ? 'sm' : undefined;
  const phaseLabel = {
    idle: '',
    uploading: '업로드 중…',
    'reading-size': '이미지 크기 확인 중…',
    confirming: `${subject} 검증 중…`,
    ready: `${subject} 업로드 완료`,
    error: '',
  }[state.phase];

  return (
    <>
      <div
        className={`${compact ? 'my-2 h-28' : 'my-3 h-40'} flex items-center justify-center overflow-hidden rounded border`}
        style={{
          backgroundColor: '#fff',
          backgroundImage:
            'conic-gradient(#e5e7eb 25%, transparent 0 50%, #e5e7eb 0 75%, transparent 0)',
          backgroundSize: '16px 16px',
        }}
      >
        {preview?.url ? (
          <img src={preview.url} alt={preview.alt} className="h-full w-full object-contain" />
        ) : (
          <span className="rounded bg-background px-2 py-1 text-sm text-muted-foreground">
            {emptyText}
          </span>
        )}
      </div>
      {preview ? (
        <p className="text-sm">
          {preview.size.widthPx} × {preview.size.heightPx} px
        </p>
      ) : null}
      <label htmlFor={id} className="sr-only">
        {fileLabel} 파일
      </label>
      <input
        ref={input}
        id={id}
        type="file"
        accept={accept}
        disabled={busy || disabled}
        className="sr-only"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = '';
          if (file) onUpload(file);
        }}
      />
      <div className={`${compact ? 'mt-2' : 'mt-3'} flex flex-wrap gap-2`}>
        <Button
          type="button"
          variant="outline"
          size={buttonSize}
          disabled={busy || disabled}
          aria-label={`${fileLabel} ${verb}`}
          onClick={() => input.current?.click()}
        >
          {actionNoun ? `${actionNoun} ${verb}` : verb}
        </Button>
        {state.phase === 'error' && state.canRetry ? (
          <Button
            type="button"
            variant="outline"
            size={buttonSize}
            onClick={onRetry}
            aria-label={`${label} 다시 업로드`}
          >
            다시 시도
          </Button>
        ) : null}
        {busy || state.phase === 'error' ? (
          <Button
            type="button"
            variant="ghost"
            size={buttonSize}
            onClick={onCancel}
            aria-label={`${label} 업로드 취소`}
          >
            취소
          </Button>
        ) : null}
        {onRemove && preview && !busy ? (
          <Button
            type="button"
            variant="ghost"
            size={buttonSize}
            onClick={onRemove}
            aria-label={`${label} 삭제`}
          >
            삭제
          </Button>
        ) : null}
      </div>
      {phaseLabel ? (
        <p role="status" className="mt-2 text-sm">
          {phaseLabel}
        </p>
      ) : null}
      {state.error ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {state.error}
        </p>
      ) : null}
    </>
  );
};
